import type { AudioFile } from '@/services/audio/audioRecordingService';

const RECORDING_FILENAME_PATTERN = /^recording-(\d{8}-\d{6})\.(.+)$/;

/**
 * Generate a standardized audio filename for upload.
 * Format: vss-(emailPrefix)-(requestId)-YYYYMMDD-HHMMSS.(ext)
 */
export function generateAudioFilename(options: {
  originalFilename: string;
  userEmail: string | undefined;
  requestId: string | number;
}): string {
  const { originalFilename, userEmail, requestId } = options;

  // Extract and sanitize email prefix
  const emailPrefix = userEmail
    ? userEmail.split('@')[0].toLowerCase().replace(/[^a-z0-9.-]/g, '')
    : 'unknown';
  const safePrefix = emailPrefix || 'unknown';

  // Extract timestamp and extension from original filename
  const match = originalFilename.match(RECORDING_FILENAME_PATTERN);
  let timestamp: string;
  let ext: string;

  if (match) {
    timestamp = match[1];
    ext = match[2];
  } else {
    // Fallback: generate new UTC timestamp, extract extension from original
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    timestamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}-${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`;
    const dotIndex = originalFilename.lastIndexOf('.');
    const rawExt = dotIndex >= 0 ? originalFilename.slice(dotIndex + 1) : 'webm';
    ext = rawExt.replace(/[^a-z0-9]/gi, '') || 'webm';
  }

  return `vss-${safePrefix}-${requestId}-${timestamp}.${ext}`;
}

/**
 * Rename an AudioFile, including its webFile if present (web platform).
 * File.name is read-only, so we create a new File object on web.
 * Does not mutate the original AudioFile.
 */
/**
 * Rename an AudioFile, including its webFile if present (web platform).
 * File.name is read-only, so we create a new File object on web.
 * Note: The original URI is preserved — we are not renaming the file on disk,
 * only the name used for upload. Native FormData uses the `name` field, not the URI filename.
 */
export function renameAudioFile(audioFile: AudioFile, newName: string): AudioFile {
  const renamed: AudioFile = { ...audioFile, name: newName };

  if (audioFile.webFile) {
    renamed.webFile = new File([audioFile.webFile], newName, {
      type: audioFile.webFile.type,
      lastModified: audioFile.webFile.lastModified,
    });
  }

  return renamed;
}
